import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ShieldAlert, Trash2, Download } from "lucide-react";
import { toast } from "sonner";
import { IncidentDetail } from '../components/incidents/IncidentDetail';
import { IncidentListItem } from '../components/incidents/IncidentListItem';
import { STATUS_STYLES } from '../components/incidents/incidentConstants';

const ReviewPanel = ({ report, onSaved, onDeleted }) => {
  const { authAxios } = useAuth();
  const [status, setStatus] = useState(report.status);
  const [notes, setNotes] = useState(report.admin_notes || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await authAxios.put(`/incident-reports/${report.id}/review`, { status, admin_notes: notes || null });
      toast.success('Incident updated');
      onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Delete this incident report permanently?')) return;
    await authAxios.delete(`/incident-reports/${report.id}`);
    toast.success('Incident deleted');
    onDeleted();
  };

  const exportPDF = async () => {
    try {
      const res = await authAxios.get(`/pdf/incident-report/${report.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `incident_report_${report.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
    } catch {
      toast.error('Failed to export PDF');
    }
  };

  return (
    <div className="mt-6 p-4 bg-stone-50 rounded-xl space-y-4" data-testid="incident-review-panel">
      <div className="space-y-2">
        <Label>Status</Label>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="incident-review-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(STATUS_STYLES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Admin notes / follow-up</Label>
        <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="incident-review-notes" />
      </div>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Button variant="ghost" className="text-rose-600" onClick={remove} data-testid="incident-delete-btn"><Trash2 className="w-4 h-4 mr-1" /> Delete</Button>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportPDF} data-testid="incident-pdf-btn"><Download className="w-4 h-4 mr-1" /> PDF</Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="incident-review-save-btn">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Save
          </Button>
        </div>
      </div>
    </div>
  );
};

export default function AdminIncidentReports() {
  const { authAxios } = useAuth();
  const [reports, setReports] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const fetchReports = useCallback(async () => {
    try {
      const res = await authAxios.get('/incident-reports', { params: filter === 'all' ? {} : { status: filter } });
      setReports(res.data);
    } finally {
      setLoading(false);
    }
  }, [authAxios, filter]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-incidents-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-stone-900 font-heading">Incident Reports</h1>
          <p className="text-stone-500 mt-1">Submitted by employees from the portal and app</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48" data-testid="incident-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.entries(STATUS_STYLES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>
      ) : reports.length === 0 ? (
        <div className="text-center py-16 text-stone-400" data-testid="admin-incidents-empty">
          <ShieldAlert className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No incident reports</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="admin-incidents-list">
          {reports.map((r) => <IncidentListItem key={r.id} report={r} onOpen={setSelected} showReporter />)}
        </div>
      )}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Incident Report</DialogTitle></DialogHeader>
          {selected && (
            <>
              <IncidentDetail report={selected} />
              <ReviewPanel
                key={selected.id}
                report={selected}
                onSaved={(r) => { setSelected(r); fetchReports(); }}
                onDeleted={() => { setSelected(null); fetchReports(); }}
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
